import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  App,
  Button,
  Col,
  Descriptions,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Switch,
  Tag,
} from "antd";
import { useSelector } from "react-redux";
import { selectAutomationAllowed } from "store/slices/main";

import GenericCard from "components/UI/GenericCard/GenericCard";
import { apiUpdateOptionStrategy } from "api/apiOptionStrategies";
import { formatMoney } from "./optionTradeUtils";
import OptionStrategyFields, {
  OPTION_PARAM_DEFAULTS,
  PARAM_KEYS,
  pickParams,
} from "./OptionStrategyFields";
import { formatDay, formatPrice } from "./optionStrategyUtils";

const OptionStrategySettings = (props) => {
  const { strategy, onSaved } = props;
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const signalsOnly = Form.useWatch("signalsOnly", form);
  const automated = Form.useWatch("automated", form);
  const automationAllowed = useSelector(selectAutomationAllowed);
  // AutoTrader only trades live, non signals-only strategies
  const canAutomate = automationAllowed && !strategy.isDemo && !signalsOnly;

  // the balance is derived from trades, so it can only change before any
  const hasTrades =
    (strategy.transactions?.length || 0) +
      (strategy.openPositions?.length || 0) +
      (strategy.pendingBuys?.length || 0) >
    0;

  const initialValues = {
    name: strategy.name,
    enabled: strategy.enabled,
    signalsOnly: strategy.signalsOnly,
    initialBalance: strategy.initialBalance,
    automated: !!strategy.automated,
    automationMode: strategy.automationMode || "full",
    buyMaxPercentDiff: strategy.automationSettings?.buyMaxPercentDiff ?? 0.5,
    orderUpdateInterval: strategy.automationSettings?.orderUpdateInterval || 15,
    ...OPTION_PARAM_DEFAULTS,
    ...Object.fromEntries(
      PARAM_KEYS.map((key) => [key, strategy.params?.[key]]).filter(
        ([, value]) => value !== undefined && value !== null,
      ),
    ),
  };

  // show the saved values whenever fresh strategy data arrives
  useEffect(() => {
    if (!isEditing) form.setFieldsValue(initialValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [strategy]);

  const onFinish = async (values) => {
    setIsSaving(true);
    try {
      const changes = {
        name: values.name,
        signalsOnly: values.signalsOnly,
        params: pickParams(values),
      };
      // only when changed here: the table's switch may have changed it since
      // this strategy was loaded
      if (values.enabled !== strategy.enabled) changes.enabled = values.enabled;
      if (!hasTrades && values.initialBalance !== strategy.initialBalance) {
        changes.initialBalance = values.initialBalance;
      }
      if (canAutomate) {
        changes.automated = !!values.automated;
        if (values.automated) {
          changes.automationMode = values.automationMode;
          changes.automationSettings = {
            buyMaxPercentDiff: values.buyMaxPercentDiff,
            orderUpdateInterval: values.orderUpdateInterval,
          };
        }
      } else if (strategy.automated) {
        changes.automated = false;
      }
      await apiUpdateOptionStrategy(strategy._id, changes);
      message.success("Strategy updated");
      setIsEditing(false);
      onSaved();
    } catch (err) {
      message.error(err?.response?.data?.message || "Failed to update");
    } finally {
      setIsSaving(false);
    }
  };

  const signal = strategy.lastSignal;

  return (
    <GenericCard title="Strategy" className="table-card" maxHeight={1200}>
      <Descriptions
        size="small"
        column={{ xxl: 6, xl: 5, lg: 4, md: 3, sm: 2, xs: 1 }}
        extra={
          isEditing ? (
            <>
              <Button
                style={{ marginInlineEnd: 8 }}
                onClick={() => {
                  form.resetFields();
                  setIsEditing(false);
                }}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                loading={isSaving}
                onClick={() => form.submit()}
              >
                Save
              </Button>
            </>
          ) : (
            <Button type="primary" onClick={() => setIsEditing(true)}>
              Edit
            </Button>
          )
        }
      >
        <Descriptions.Item label="Symbol">{strategy.symbol}</Descriptions.Item>
        <Descriptions.Item label="Model">
          {strategy.models?.map((m) => m.name).join(", ") || "-"}
        </Descriptions.Item>
        <Descriptions.Item label="Mode">
          {strategy.signalsOnly ? (
            <Tag>Signals only</Tag>
          ) : strategy.isDemo ? (
            <Tag color="red">Demo</Tag>
          ) : (
            <Tag color="green">Real</Tag>
          )}
        </Descriptions.Item>
        {!strategy.signalsOnly && (
          <>
            <Descriptions.Item label="Equity">
              ${formatMoney(strategy.equity)}
            </Descriptions.Item>
            <Descriptions.Item label="Balance">
              ${formatMoney(strategy.balance)}
            </Descriptions.Item>
          </>
        )}
        <Descriptions.Item label="Early signal (15:50)">
          {signal?.date ? (
            <>
              <Tag color={signal.prediction === "buy" ? "green" : "default"}>
                {signal.prediction}
              </Tag>
              {formatDay(signal.date)} @ {formatPrice(signal.close)}
            </>
          ) : (
            "-"
          )}
        </Descriptions.Item>
      </Descriptions>

      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={initialValues}
        style={{ marginTop: 16 }}
      >
        <Row gutter={16}>
          <Col xs={24} sm={12} lg={8}>
            <Form.Item
              name="name"
              label="Name"
              rules={[{ required: true, message: "Name is required" }]}
            >
              <Input disabled={!isEditing} />
            </Form.Item>
          </Col>
          <Col>
            <Form.Item name="enabled" label="Enabled" valuePropName="checked">
              <Switch disabled={!isEditing} />
            </Form.Item>
          </Col>
          <Col>
            <Form.Item
              name="signalsOnly"
              label="Signals only"
              valuePropName="checked"
              tooltip="Track signals and get the email, without suggested trades"
            >
              <Switch disabled={!isEditing} />
            </Form.Item>
          </Col>
          {!signalsOnly && (
            <Col xs={24} sm={12} lg={6}>
              <Form.Item
                name="initialBalance"
                label="Initial balance"
                tooltip={
                  hasTrades
                    ? "Can't change once the strategy has trades"
                    : undefined
                }
                rules={[{ required: true, message: "Required" }]}
              >
                <InputNumber
                  min={1}
                  prefix="$"
                  style={{ width: "100%" }}
                  disabled={!isEditing || hasTrades}
                />
              </Form.Item>
            </Col>
          )}
        </Row>
        <OptionStrategyFields disabled={!isEditing} />
        {canAutomate && (
          <Row gutter={16}>
            <Col>
              <Form.Item
                name="automated"
                label="Automated trading"
                valuePropName="checked"
                tooltip="AutoTrader places the buys and exits at IBKR (15:50 ET signals, filled before the 16:00 close). Expiring options are closed at 15:30 ET on their expiration day."
              >
                <Switch disabled={!isEditing} />
              </Form.Item>
            </Col>
            {automated && (
              <>
                <Col>
                  <Form.Item
                    name="automationMode"
                    label="Buy approval"
                    tooltip="Semi: each buy is sent to Telegram (and shown here) for your approval first. Exits stay automatic."
                  >
                    <Select
                      disabled={!isEditing}
                      style={{ width: 200 }}
                      options={[
                        { value: "full", label: "Full - buy automatically" },
                        { value: "semi", label: "Semi - ask me first" },
                      ]}
                    />
                  </Form.Item>
                </Col>
                <Col>
                  <Form.Item
                    name="buyMaxPercentDiff"
                    label="Max % difference from buy point"
                    tooltip="The highest premium AutoTrader pays, above the 15:50 ask"
                  >
                    <InputNumber
                      min={0}
                      max={100}
                      prefix="%"
                      disabled={!isEditing}
                    />
                  </Form.Item>
                </Col>
                <Col>
                  <Form.Item
                    name="orderUpdateInterval"
                    label="Price update interval"
                  >
                    <InputNumber
                      min={5}
                      addonAfter="Seconds"
                      disabled={!isEditing}
                    />
                  </Form.Item>
                </Col>
              </>
            )}
          </Row>
        )}
      </Form>
    </GenericCard>
  );
};

OptionStrategySettings.propTypes = {
  strategy: PropTypes.object.isRequired,
  onSaved: PropTypes.func.isRequired,
};

export default OptionStrategySettings;
